import { Paper, Title, Badge, Text, Stack, Group, Loader, Center, Tooltip } from '@mantine/core';
import { useAlerts } from './usePrometheus';

const HIDDEN_ALERTS = ['Watchdog', 'InfoInhibitor'];

const SEVERITY_ORDER: Record<string, number> = { critical: 0, warning: 1, info: 2 };

function severityColor(severity: string) {
  if (severity === 'critical') return 'red';
  if (severity === 'warning') return 'orange';
  return 'blue';
}

export function AlertsPanel() {
  const { data: alerts, isLoading, error } = useAlerts();

  if (error) {
    return (
      <Paper shadow="xs" p="md" withBorder>
        <Title order={4} mb="md">Alerts</Title>
        <Text c="red">Fehler beim Laden</Text>
      </Paper>
    );
  }

  if (isLoading || !alerts) {
    return (
      <Paper shadow="xs" p="md" withBorder>
        <Title order={4} mb="md">Alerts</Title>
        <Center><Loader size="sm" /></Center>
      </Paper>
    );
  }

  const visibleAlerts = alerts
    .filter((a: any) => !HIDDEN_ALERTS.includes(a.labels?.alertname))
    .sort((a: any, b: any) => {
      const stateOrder = (a.state === 'firing' ? 0 : 1) - (b.state === 'firing' ? 0 : 1);
      if (stateOrder !== 0) return stateOrder;
      return (SEVERITY_ORDER[a.labels?.severity] ?? 9) - (SEVERITY_ORDER[b.labels?.severity] ?? 9);
    });

  const firingCount = visibleAlerts.filter((a: any) => a.state === 'firing').length;
  const pendingCount = visibleAlerts.filter((a: any) => a.state === 'pending').length;

  return (
    <Paper shadow="xs" p="md" withBorder>
      <Group justify="space-between" mb="md">
        <Title order={4}>Alerts</Title>
        <Group gap="xs">
          {firingCount > 0 && <Badge color="red" variant="light">{firingCount} firing</Badge>}
          {pendingCount > 0 && <Badge color="yellow" variant="light">{pendingCount} pending</Badge>}
          {firingCount === 0 && pendingCount === 0 && <Badge color="green" variant="light">OK</Badge>}
        </Group>
      </Group>
      <Stack gap="xs" style={{ maxHeight: 300, overflow: 'auto' }}>
        {visibleAlerts.length === 0 && (
          <Text c="dimmed" size="sm">Keine aktiven Alerts</Text>
        )}
        {visibleAlerts.map((alert: any, i: number) => (
          <Tooltip
            key={i}
            label={alert.annotations?.description || alert.annotations?.summary || 'Keine Beschreibung verfügbar'}
            multiline
            w={300}
            withArrow
          >
            <Paper p="xs" withBorder>
              <Group justify="space-between" wrap="nowrap">
                <Group gap="xs" wrap="nowrap" style={{ overflow: 'hidden' }}>
                  <Badge
                    size="xs"
                    color={severityColor(alert.labels?.severity)}
                    variant="filled"
                    style={{ flexShrink: 0 }}
                  >
                    {alert.labels?.severity || alert.state}
                  </Badge>
                  <Text size="sm" fw={500} lineClamp={1}>
                    {alert.labels?.alertname ?? 'Unknown'}
                  </Text>
                </Group>
                <Badge
                  size="xs"
                  color={alert.state === 'firing' ? 'red' : 'yellow'}
                  variant="outline"
                  style={{ flexShrink: 0 }}
                >
                  {alert.state}
                </Badge>
              </Group>
              {alert.annotations?.summary && (
                <Text size="xs" c="dimmed" lineClamp={1} mt={2}>
                  {alert.annotations.summary}
                </Text>
              )}
            </Paper>
          </Tooltip>
        ))}
      </Stack>
    </Paper>
  );
}
